/**
 * PHONE LIST EDITOR — Gestione inline della lista telefoni di un'entita'.
 *
 * Pattern identico a EmailListEditor: ogni phone e' una riga con
 * EditableField text + Switch primary + IconButton delete. Pulsante
 * "+ Aggiungi" in fondo. Logica handle-update / auto-promote / toast
 * delegata a `useSimpleListItemEditor` (condiviso con email).
 *
 * Nessuna validazione formale sul numero (accettiamo qualsiasi formato
 * internazionale libero).
 */

import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import AddIcon from '@mui/icons-material/Add';
import { useAppDispatch } from '../store/hooks';
import { ConfirmDialog } from './ConfirmDialog';
import { useConfirmDialog } from './useConfirmDialog';
import { EditableField } from './EditableField';
import { NewSimpleListItemInput } from './NewSimpleListItemInput';
import { useSimpleListItemEditor } from './useSimpleListItemEditor';
import type { Phone, PhoneOwnerClassName } from '../entities/phone/phoneTypes';
import { addPhone, updatePhone, removePhone } from '../entities/phone/phoneThunks';

interface PhoneListEditorProps {
  className: PhoneOwnerClassName;
  classPK: number;
  phones: Phone[];
}

export function PhoneListEditor({ className, classPK, phones }: PhoneListEditorProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  // FILO-18: il cestino chiede conferma, la cancellazione non si può disfare.
  const confirm = useConfirmDialog();

  const {
    isAdding,
    setIsAdding,
    handleUpdateValue,
    handleSetPrimary,
    handleRemove,
    handleAdd,
  } = useSimpleListItemEditor<Phone>({
    items: phones,
    getId: (p) => p.phoneId,
    getPrimary: (p) => p.primary,
    getValue: (p) => p.number,
    onAdd: (number, primary) =>
      dispatch(addPhone({ className, classPK, number, primary })).unwrap(),
    onUpdate: (p, number, primary) =>
      dispatch(updatePhone({ phoneId: p.phoneId, className, classPK, number, primary })).unwrap(),
    onRemove: (p) =>
      dispatch(removePhone({ phoneId: p.phoneId, className, classPK })).unwrap(),
  });

  return (
    <div className="list-editor">
      <div className="list-editor__label">{t('common.phone')}</div>
      {phones.map((phone) => (
        <div key={phone.phoneId} className={`list-editor__row le-item${phone.primary ? ' is-primary' : ''}`}>
          <IconButton
            className="le-star"
            size="small"
            onClick={() => handleSetPrimary(phone)}
            disabled={phone.primary}
            aria-label={phone.primary ? t('editableField.primary') : t('editableField.setAsPrimary')}
            title={phone.primary ? t('editableField.primary') : t('editableField.setAsPrimary')}
          >
            {phone.primary ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
          </IconButton>
          <div className="list-editor__row-value">
            <EditableField
              value={phone.number}
              variant={{ kind: 'text' }}
              required
              onCommit={(v) => handleUpdateValue(phone, v)}
              label={t('common.phone')}
            />
          </div>
          <IconButton
            className="le-remove"
            size="small"
            onClick={() => confirm.requestConfirm(
              t('editableField.removePhoneConfirm', { value: phone.number }),
              () => handleRemove(phone),
            )}
            aria-label={t('common.remove')}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </div>
      ))}
      {isAdding ? (
        <NewSimpleListItemInput
          onConfirm={handleAdd}
          onCancel={() => setIsAdding(false)}
          placeholder="+39 339 1234567"
        />
      ) : (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setIsAdding(true)}
          className="btn-lowercase mt-xs"
        >
          {t('common.add')}
        </Button>
      )}
      <ConfirmDialog
        open={confirm.isOpen}
        message={confirm.message}
        {...confirm.options}
        onConfirm={confirm.confirm}
        onCancel={confirm.cancel}
      />
    </div>
  );
}
